/**
 * useDraftText — a long free-text field that a refetch can never wipe.
 *
 * Written after a rep lost twenty minutes of call notes: the lead file was torn
 * down and rebuilt under them (see useApiClient), and the notes box only ever
 * held its text in local state until blur. The rules here:
 *
 *   - DIRTY WINS. Text typed and not yet saved is never replaced by the server
 *     value. A server change is taken only while the field is clean.
 *   - AUTOSAVE. ~1.5s after typing stops, and at once on blur. One save in
 *     flight at a time; what is typed meanwhile goes after it. A failed save
 *     keeps the text on screen and says so (status 'error', retry()).
 *   - ONE ACTIVITY ENTRY PER EDITING SESSION. A session runs from the first
 *     keystroke to blur; the first save of it is flagged (`firstOfSession`) so
 *     the server logs it once, not per keystroke or per debounce.
 *   - DRAFT BACKUP. Every keystroke is mirrored to localStorage under the lead
 *     and field; a successful save clears it. On load, a backup is put back if
 *     it is newer than the saved value (the field has not changed on the server
 *     since the draft began, or the draft is later than the lead's last write).
 *     An older one is OFFERED, never silently dropped.
 *   - LEAVE-PAGE WARNING while anything is unsaved (useUnsavedGuard).
 *
 * Without `save`, the field is backup + warning only (a composer whose text is
 * sent by its own button).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type DraftStatus = 'idle' | 'saving' | 'saved' | 'error';

interface Backup {
  v: string;
  /** The saved value the draft was typed over. */
  base: string;
  /** ms epoch of the last keystroke. */
  at: number;
}

export const draftKey = (leadId: string, field: string) => `mf:draft:${leadId}:${field}`;

export function readDraft(key: string | null): Backup | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const b = JSON.parse(raw);
    return typeof b?.v === 'string' && typeof b?.at === 'number' ? { v: b.v, base: String(b.base ?? ''), at: b.at } : null;
  } catch {
    return null;
  }
}
export function writeDraft(key: string | null, v: string, base: string) {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify({ v, base, at: Date.now() })); } catch { /* storage full or blocked: the field still works */ }
}
export function clearDraft(key: string | null) {
  if (!key) return;
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

// ---- leave-page warning: one listener for every unsaved field on the page ----
const unsavedOwners = new Set<object>();
let listening = false;
function onBeforeUnload(e: BeforeUnloadEvent) {
  if (unsavedOwners.size === 0) return;
  e.preventDefault();
  e.returnValue = ''; // older browsers need it set to show the prompt
}
/** While `unsaved` is true, closing or reloading the tab asks first. */
export function useUnsavedGuard(unsaved: boolean) {
  const owner = useRef({}).current;
  useEffect(() => {
    if (!listening) { window.addEventListener('beforeunload', onBeforeUnload); listening = true; }
    if (unsaved) unsavedOwners.add(owner); else unsavedOwners.delete(owner);
    return () => { unsavedOwners.delete(owner); };
  }, [unsaved, owner]);
}

interface Options {
  /** draftKey(leadId, field); null turns the backup off. */
  storageKey: string | null;
  /** The value the server holds ('' for null). */
  serverValue: string;
  /** The record's last write (leads.updated_at), to date a backup against. */
  serverUpdatedAt?: string | null;
  /** Persist the text. Must throw on failure. */
  save?: (value: string, firstOfSession: boolean) => Promise<void>;
  debounceMs?: number;
}

export function useDraftText({ storageKey, serverValue, serverUpdatedAt, save, debounceMs = 1500 }: Options) {
  const [draft, setDraftState] = useState(serverValue);
  const [status, setStatus] = useState<DraftStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  /** A backup older than the saved value: shown as an offer, not applied. */
  const [offer, setOffer] = useState<Backup | null>(null);
  /** When a backup was put back on load (for the notice). */
  const [restoredAt, setRestoredAt] = useState<number | null>(null);

  const draftRef = useRef(serverValue);
  const savedRef = useRef(serverValue);              // what the server is known to hold
  const timer = useRef<number | null>(null);
  const inFlight = useRef(false);
  const again = useRef(false);
  const session = useRef(0);                         // bumped on the first keystroke after blur
  const sessionOpen = useRef(false);
  const loggedSession = useRef(-1);
  const saveRef = useRef(save);
  saveRef.current = save;
  const keyRef = useRef(storageKey);
  keyRef.current = storageKey;

  const setDraft = (v: string) => { draftRef.current = v; setDraftState(v); };
  const clearTimer = () => { if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null; } };

  const flush = useCallback(async (): Promise<void> => {
    clearTimer();
    const doSave = saveRef.current;
    if (!doSave) return;
    if (inFlight.current) { again.current = true; return; }
    const v = draftRef.current;
    if (v === savedRef.current) {
      clearDraft(keyRef.current);
      return;
    }
    const sid = session.current;
    const first = loggedSession.current !== sid;
    inFlight.current = true;
    setStatus('saving');
    setError(null);
    try {
      await doSave(v, first);
      savedRef.current = v;
      loggedSession.current = sid;
      if (draftRef.current === v) {
        clearDraft(keyRef.current);
        setStatus('saved');
      }
    } catch (e: any) {
      setStatus('error');
      setError(e?.message || 'Not saved');
    } finally {
      inFlight.current = false;
      // Typed (and debounced, or blurred) while this was saving: send that now.
      if (again.current) { again.current = false; void flush(); }
    }
  }, []);

  const schedule = useCallback(() => {
    clearTimer();
    if (!saveRef.current) return;
    timer.current = window.setTimeout(() => { timer.current = null; void flush(); }, debounceMs);
  }, [flush, debounceMs]);

  // DIRTY WINS: take a new server value only while nothing is unsaved.
  useEffect(() => {
    const clean = draftRef.current === savedRef.current && !inFlight.current;
    savedRef.current = serverValue;
    if (clean) setDraft(serverValue);
    else if (draftRef.current === serverValue) clearDraft(keyRef.current);
  }, [serverValue]);

  // Put back a backup on load (and on moving to another lead/field).
  useEffect(() => {
    setOffer(null);
    setRestoredAt(null);
    const b = readDraft(storageKey);
    if (!b) return;
    if (b.v === serverValue) { clearDraft(storageKey); return; }
    const savedAt = serverUpdatedAt ? Date.parse(serverUpdatedAt) : NaN;
    const newer = b.base === serverValue || !Number.isFinite(savedAt) || b.at > savedAt;
    if (newer) {
      setDraft(b.v);
      setRestoredAt(b.at);
      schedule();
    } else {
      setOffer(b);
    }
    // Only on a new key: a later serverValue is handled by the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Leaving with a save pending: send it (the backup stays until it lands).
  useEffect(() => () => {
    if (timer.current != null || draftRef.current !== savedRef.current) void flush();
  }, [flush]);

  const onChange = (v: string) => {
    if (!sessionOpen.current) { sessionOpen.current = true; session.current += 1; }
    setDraft(v);
    setOffer(null);
    if (v === savedRef.current) clearDraft(keyRef.current);
    else writeDraft(keyRef.current, v, savedRef.current);
    if (status === 'saved') setStatus('idle');
    schedule();
  };
  const onBlur = () => {
    sessionOpen.current = false;
    void flush();
  };
  const restoreOffer = () => {
    if (!offer) return;
    onChange(offer.v);
    setRestoredAt(offer.at);
  };
  const discardOffer = () => { clearDraft(keyRef.current); setOffer(null); };
  /** For a field without autosave: its own button sent it; drop the backup. */
  const reset = (v = '') => { clearTimer(); savedRef.current = v; setDraft(v); clearDraft(keyRef.current); setRestoredAt(null); };

  const unsaved = draft !== savedRef.current || status === 'saving';
  useUnsavedGuard(unsaved);

  return {
    value: draft, onChange, onBlur, status, error, retry: () => void flush(),
    unsaved, restoredAt, offer, restoreOffer, discardOffer, reset,
  };
}
